/**
 * Operación: salud del procesamiento. Grabaciones por estado, atascadas, fallas
 * por código, tiempo hasta las notas (p50/p90) y reintento en un clic.
 */
import { Link } from 'react-router'
import { recordingErrorLabel, type RecordingStatus } from '@eco/shared'
import { errorText, useApp, useToast } from '../../components/AppContext'
import { GraficoSerie } from '../../components/charts'
import { ErrorBox, Skeleton, StatusBadge } from '../../components/ui'
import { diaCorto, fechaHora } from '../../lib/adminFormat'
import { useAsync, useDocumentTitle, usePolling } from '../../lib/hooks'
import { Kpi, Seccion } from './common'

const minutos = (n: number) => `${Math.round(n * 10) / 10} min`

const EN_PROCESO: RecordingStatus[] = ['captured', 'queued', 'processing', 'merging', 'summarizing']

export function Operacion() {
  useDocumentTitle('Operación · Administración')
  const { api } = useApp()
  const toast = useToast()
  const op = useAsync(() => api.adminOperacion(), [api])
  // Se refresca sola mientras está abierta (y la pestaña visible)
  usePolling(() => void op.reload(), 30_000, true)

  async function reintentar(id: string) {
    try {
      await api.adminRetry(id)
      toast('Reencolada')
      await op.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  if (op.error) return <ErrorBox error={op.error} onRetry={() => void op.reload()} />
  if (!op.data) return <Skeleton h={240} />
  const d = op.data
  const enProceso = EN_PROCESO.reduce((n, e) => n + (d.estados[e] ?? 0), 0)
  const fallidas7 = d.fallidasPorCodigo.reduce((n, f) => n + f.n, 0)
  const conReuniones = d.procesamiento.some((p) => p.n > 0)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="kpis">
        <Kpi label="En proceso ahora" valor={enProceso} />
        <Kpi
          label="Atascadas (+1 h)"
          valor={d.atascadas}
          nota={d.atascadas ? 'revisar' : 'todo bien'}
        />
        <Kpi label="Fallidas (7 días)" valor={fallidas7} />
        <Kpi label="Esperando horas" valor={d.estados.retenida ?? 0} nota="plan sin horas" />
        <Kpi label="En espera" valor={d.estados.held ?? 0} nota="control de costos" />
        <Kpi
          label="Integraciones"
          valor={d.webhooks.conFallos}
          nota={`con fallos · ${d.webhooks.apagados} apagadas`}
        />
      </div>

      <div className="admin-grid">
        <div className="span-8">
          <GraficoSerie
            titulo="Tiempo hasta las notas"
            subtitulo="Mediana por día (minutos desde que terminó la reunión). El p90 está en la tabla."
            tipo="barras"
            puntos={
              conReuniones
                ? d.procesamiento.map((p) => ({ etiqueta: p.dia, valor: p.n ? p.p50Min : 0 }))
                : []
            }
            formato={(n) => (n ? minutos(n) : 'sin reuniones')}
            formatoEje={minutos}
            nombreValor="Mediana"
            sinTotal
          />
        </div>
        <div className="span-4">
          <Seccion titulo="Fallas por código (7 días)">
            {d.fallidasPorCodigo.length === 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                Ninguna falla.
              </p>
            ) : (
              <table className="simple">
                <caption className="sr">Fallas por código en 7 días</caption>
                <tbody>
                  {d.fallidasPorCodigo.map((f) => (
                    <tr key={f.codigo}>
                      <td>
                        {f.codigo === 'sin_codigo' ? 'Sin código' : recordingErrorLabel(f.codigo)}
                        <div className="faint mono" style={{ fontSize: 11 }}>
                          {f.codigo}
                        </div>
                      </td>
                      <td className="num">{f.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>
        </div>
      </div>

      {conReuniones && (
        <details className="card">
          <summary style={{ cursor: 'pointer', fontWeight: 550 }}>
            Detalle del procesamiento por día
          </summary>
          <table className="simple" style={{ marginTop: 10 }}>
            <caption className="sr">Tiempo hasta las notas por día</caption>
            <thead>
              <tr>
                <th scope="col">Día</th>
                <th scope="col" className="num">
                  Reuniones
                </th>
                <th scope="col" className="num">
                  Mediana
                </th>
                <th scope="col" className="num">
                  p90
                </th>
              </tr>
            </thead>
            <tbody>
              {d.procesamiento.map((p) => (
                <tr key={p.dia}>
                  <td>{diaCorto(p.dia)}</td>
                  <td className="num">{p.n}</td>
                  <td className="num">{p.n ? minutos(p.p50Min) : '—'}</td>
                  <td className="num">{p.n ? minutos(p.p90Min) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <Seccion titulo="Grabaciones con problemas">
        {d.recientesConProblemas.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            No hay grabaciones fallidas ni retenidas.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <caption className="sr">Grabaciones con problemas</caption>
              <thead>
                <tr>
                  <th scope="col">Creada</th>
                  <th scope="col">Persona</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Error</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {d.recientesConProblemas.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {fechaHora(r.creadoEn)}
                      <div className="faint mono" style={{ fontSize: 11 }}>
                        {r.id.slice(0, 8)}
                      </div>
                    </td>
                    <td>
                      {r.email ? (
                        <Link
                          className="link"
                          to={`/admin/usuarios?q=${encodeURIComponent(r.email)}`}
                        >
                          {r.email}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <StatusBadge estado={r.estado} />
                    </td>
                    <td className="faint">{r.errorCode ?? ''}</td>
                    <td>
                      {(r.estado === 'failed' || r.estado === 'held') && (
                        <button className="btn sm" onClick={() => void reintentar(r.id)}>
                          Reintentar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Seccion>
    </div>
  )
}
